import { t } from "../i18n";
import type { DemoState, MethodSnapshot, RegionEntry } from "../state";
import { buildMethodSnapshot } from "../util/method-patch";

export function cloneMethodSnapshot(
  snapshot: MethodSnapshot | null,
): MethodSnapshot | null {
  return snapshot === null ? null : { ...snapshot };
}

function methodSnapshotsEqual(
  a: MethodSnapshot | null,
  b: MethodSnapshot | null,
): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function resolveEntryCurrentSnapshot(
  state: DemoState,
  entry: RegionEntry,
): MethodSnapshot {
  if (entry.override !== null) return cloneMethodSnapshot(entry.override)!;
  const stack =
    entry.stackId !== null
      ? state.regionStacks.find((candidate) => candidate.uuid === entry.stackId)
      : undefined;
  if (stack?.override !== undefined && stack.override !== null) {
    return cloneMethodSnapshot(stack.override)!;
  }
  return buildMethodSnapshot(state);
}

export function chooseMergeSourceStyle(
  state: DemoState,
  targetUuid: string,
  sourceUuids: string[],
): MethodSnapshot | null {
  const targetEntry = state.regionEntries.find((entry) => entry.uuid === targetUuid);
  const sourceEntry = sourceUuids
    .map((uuid) => state.regionEntries.find((entry) => entry.uuid === uuid))
    .find((entry): entry is RegionEntry => entry !== undefined);
  if (targetEntry === undefined || sourceEntry === undefined) return null;

  const targetSnapshot = resolveEntryCurrentSnapshot(state, targetEntry);
  const sourceSnapshot = resolveEntryCurrentSnapshot(state, sourceEntry);
  if (methodSnapshotsEqual(targetSnapshot, sourceSnapshot)) return null;

  return window.confirm(t("confirm.region.mergeStyle"))
    ? null
    : sourceSnapshot;
}

export type StackStyleChoice = "stack" | "area" | null;

export function chooseStackStyle(
  state: DemoState,
  stackUuid: string,
  memberUuids: string[],
): StackStyleChoice {
  const stack = state.regionStacks.find((candidate) => candidate.uuid === stackUuid);
  if (stack === undefined) return null;
  const memberSet = new Set(memberUuids);
  const entries = state.regionEntries.filter((entry) => memberSet.has(entry.uuid));
  if (entries.length === 0) return null;

  const stackSnapshot = stack.override ?? buildMethodSnapshot(state);
  const hasConflict = entries.some(
    (entry) =>
      !methodSnapshotsEqual(resolveEntryCurrentSnapshot(state, entry), stackSnapshot),
  );
  if (!hasConflict) return null;

  return window.confirm(t("confirm.stack.addStyle")) ? "stack" : "area";
}

export function applyStackStyleChoice(
  state: DemoState,
  entries: RegionEntry[],
  memberUuids: string[],
  choice: StackStyleChoice,
): RegionEntry[] {
  if (choice === null) return entries;
  const memberSet = new Set(memberUuids);
  const areaSnapshots = new Map<string, MethodSnapshot>();
  if (choice === "area") {
    for (const entry of state.regionEntries) {
      if (memberSet.has(entry.uuid)) {
        areaSnapshots.set(entry.uuid, resolveEntryCurrentSnapshot(state, entry));
      }
    }
  }

  return entries.map((entry) => {
    if (!memberSet.has(entry.uuid)) return entry;
    if (choice === "stack") return { ...entry, override: null };
    const snapshot = areaSnapshots.get(entry.uuid);
    return snapshot === undefined
      ? entry
      : { ...entry, override: cloneMethodSnapshot(snapshot) };
  });
}
